import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Settings as SettingsIcon, Key, Save, Loader2, Eye, EyeOff,
  Instagram, Facebook, Linkedin, Youtube, Twitter,
  CheckCircle, XCircle, ExternalLink, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const socialPlatforms = [
  { 
    id: 'instagram', 
    name: 'Instagram / Facebook (Meta)', 
    icon: Instagram, 
    color: 'text-pink-500',
    fields: [
      { key: 'meta_app_id', label: 'App ID', placeholder: 'Your Meta App ID' },
      { key: 'meta_app_secret', label: 'App Secret', placeholder: 'Your Meta App Secret', secret: true },
      { key: 'meta_access_token', label: 'Page Access Token', placeholder: 'Page Access Token (requires pages_manage_posts permission)', secret: true },
      { key: 'meta_page_id', label: 'Facebook Page ID', placeholder: 'Your Facebook Page ID (numeric, e.g., 123456789)' },
    ],
    docsUrl: 'https://developers.facebook.com/docs/pages-api/posts/',
    helpText: 'To post to Facebook: 1) Create/link a Facebook Page to your app, 2) Get Page Access Token with pages_manage_posts permission from Graph API Explorer, 3) Copy your Page ID from the Page\'s About section'
  },
  { 
    id: 'linkedin', 
    name: 'LinkedIn', 
    icon: Linkedin, 
    color: 'text-blue-600',
    fields: [
      { key: 'linkedin_client_id', label: 'Client ID', placeholder: 'Your LinkedIn Client ID' },
      { key: 'linkedin_client_secret', label: 'Client Secret', placeholder: 'Your LinkedIn Client Secret', secret: true },
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/linkedin/shared/authentication/authorization-code-flow'
  },
  { 
    id: 'twitter', 
    name: 'X (Twitter)', 
    icon: Twitter, 
    color: 'text-zinc-400',
    fields: [
      { key: 'twitter_api_key', label: 'API Key', placeholder: 'Your Twitter API Key' },
      { key: 'twitter_api_secret', label: 'API Secret', placeholder: 'Your Twitter API Secret', secret: true },
      { key: 'twitter_access_token', label: 'Access Token', placeholder: 'Your access token', secret: true },
      { key: 'twitter_access_secret', label: 'Access Token Secret', placeholder: 'Your access token secret', secret: true },
    ],
    docsUrl: 'https://developer.twitter.com/en/docs/authentication/oauth-1-0a'
  },
  { 
    id: 'tiktok', 
    name: 'TikTok', 
    icon: null, 
    color: 'text-white',
    fields: [
      { key: 'tiktok_client_key', label: 'Client Key', placeholder: 'Your TikTok Client Key' },
      { key: 'tiktok_client_secret', label: 'Client Secret', placeholder: 'Your TikTok Client Secret', secret: true },
    ],
    docsUrl: 'https://developers.tiktok.com/doc/login-kit-web'
  },
  { 
    id: 'youtube', 
    name: 'YouTube', 
    icon: Youtube, 
    color: 'text-red-500',
    fields: [
      { key: 'youtube_client_id', label: 'Client ID', placeholder: 'Your Google OAuth Client ID' },
      { key: 'youtube_client_secret', label: 'Client Secret', placeholder: 'Your Google OAuth Client Secret', secret: true },
    ],
    docsUrl: 'https://developers.google.com/youtube/v3/getting-started'
  },
];

const aiProviders = [
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT-5.2 for text generation and content creation',
    fields: [
      { key: 'openai_api_key', label: 'API Key', placeholder: 'sk-...', secret: true },
    ],
    docsUrl: 'https://platform.openai.com/api-keys'
  },
];

export default function Settings() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [connectionStatus, setConnectionStatus] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/api/marketing/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || {});
        setConnectionStatus(data.connections || {});
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/marketing/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ settings })
      });

      if (response.ok) {
        toast.success('Settings saved successfully!');
        fetchSettings();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleSecretVisibility = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">Configure your marketing automation integrations</p>
        </div>
        <button 
          onClick={saveSettings}
          disabled={saving}
          className="marketing-btn-primary flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </button>
      </div>

      {/* AI Provider Settings */}
      <div className="marketing-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
            <Key className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold">AI Provider</h2>
            <p className="text-sm text-muted-foreground">Configure your AI text generation provider</p>
          </div>
        </div>

        {aiProviders.map((provider) => (
          <div key={provider.id} className="p-4 bg-secondary/30 rounded-lg border border-border">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">{provider.name}</h3>
                <p className="text-sm text-muted-foreground">{provider.description}</p>
              </div>
              <a 
                href={provider.docsUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary text-sm hover:underline flex items-center gap-1"
              >
                Get API Key <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            
            <div className="space-y-3">
              {provider.fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium mb-1">{field.label}</label>
                  <div className="relative">
                    <input
                      type={field.secret && !showSecrets[field.key] ? 'password' : 'text'}
                      value={settings[field.key] || ''}
                      onChange={(e) => updateSetting(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="marketing-input pr-10"
                    />
                    {field.secret && (
                      <button
                        type="button"
                        onClick={() => toggleSecretVisibility(field.key)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showSecrets[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Social Media API Settings */}
      <div className="marketing-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-semibold">Social Media Integrations</h2>
            <p className="text-sm text-muted-foreground">Connect your social media accounts for automation</p>
          </div>
        </div>

        <div className="space-y-4">
          {socialPlatforms.map((platform) => {
            const isConnected = connectionStatus[platform.id];
            const Icon = platform.icon;
            
            return (
              <div key={platform.id} className="p-4 bg-secondary/30 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {Icon ? (
                      <Icon className={`w-6 h-6 ${platform.color}`} />
                    ) : (
                      <span className="text-lg font-bold">TT</span>
                    )}
                    <div>
                      <h3 className="font-medium">{platform.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {isConnected ? (
                          <span className="flex items-center gap-1 text-xs text-emerald-400">
                            <CheckCircle className="w-3 h-3" /> Connected
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <XCircle className="w-3 h-3" /> Not connected
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <a 
                    href={platform.docsUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary text-sm hover:underline flex items-center gap-1"
                  >
                    Docs <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                
                <div className="grid md:grid-cols-2 gap-3">
                  {platform.fields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs font-medium mb-1">{field.label}</label>
                      <div className="relative">
                        <input
                          type={field.secret && !showSecrets[field.key] ? 'password' : 'text'}
                          value={settings[field.key] || ''}
                          onChange={(e) => updateSetting(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          className="marketing-input text-sm pr-10"
                        />
                        {field.secret && (
                          <button
                            type="button"
                            onClick={() => toggleSecretVisibility(field.key)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showSecrets[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Help Text for Facebook Setup */}
                {platform.helpText && (
                  <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-xs text-amber-400 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{platform.helpText}</span>
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="mt-4 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium text-sm">API Keys Security</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your API keys are encrypted and stored securely. Never share your API keys publicly. 
                You can revoke and regenerate keys from the respective platform's developer console.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="marketing-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 ai-gradient rounded-lg flex items-center justify-center text-primary-foreground font-bold">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div>
            <h3 className="font-semibold">{user?.name || 'User'}</h3>
            <p className="text-sm text-muted-foreground">{user?.email || ''}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Connected to AISalesTask CRM. Your marketing data syncs with your CRM leads and customers.
        </p>
      </div>
    </div>
  );
}
